// Route-level RBAC gate. Wrap a route's element in <RouteAccessGuard feature={...}>
// and it renders the page only when the user can `view` that feature; otherwise it
// raises the shared no-access toaster (once) and shows an inline "restricted" panel.
//
// Why an inline panel instead of a <Redirect>: with every route guarded, redirecting
// a denied user to another guarded route (e.g. /dashboard they also can't view) would
// bounce again and risk a redirect loop. Staying put with a clear message is loop-safe
// and keeps the nav usable.
//
// Unlisted users get a full-access ability (see abilities.js), so `can(view, feature)`
// is true for them and the guard is a no-op — only explicitly-restricted users are gated.

import React, { useEffect } from 'react';
import { useAbility } from './AbilityContext';
import { FEATURE_ACTIONS } from './features';
import { notifyNoAccess } from './notifyNoAccess';

const NoAccessPanel = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      padding: 24,
      textAlign: 'center',
      color: '#555',
    }}
  >
    <h2 style={{ marginBottom: 8 }}>Access restricted</h2>
    <p style={{ margin: 0 }}>You do not have permission to view this page.</p>
  </div>
);

const RouteAccessGuard = ({ feature, children }) => {
  const ability = useAbility();
  // No feature to check, or (defensively) no ability on context => allow through.
  const allowed = !feature || !ability || ability.can(FEATURE_ACTIONS.VIEW, feature);

  useEffect(() => {
    if (!allowed) notifyNoAccess();
  }, [allowed, feature]);

  return allowed ? children : <NoAccessPanel />;
};

export default RouteAccessGuard;
